// src/components/LabelRenderer.js
// Core shared label renderer — used in preview, designer, and detail page.
// Supports two layout formats:
//   NEW: design.columns[] → panels span full height; each panel has rows[] or blocks[]
//   LEGACY: design.rows[] → rows span full width; each row has columns[] with blocks[]
/* eslint-disable react/react-in-jsx-scope */
import { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';

// ─── FDA 21 CFR 101.9(c)(8)(iii) rounding for vitamins & minerals ────────────
// <2% → '<2', 2–10% → nearest 2%, 10–50% → nearest 5%, >50% → nearest 10%. Takes the RAW
// (unrounded) %DV ratio — stored as a string by LabelDetailPage.js's dvPct(), so coerce through
// Number() rather than comparing the raw string (e.g. a strict `dv === 0` check against the
// string "0" would silently fail and fall through to the wrong band).
const fdaRoundMicroDV = (dv) => {
  if (dv == null || dv === '') return null;
  const n = Number(dv);
  if (isNaN(n)) return null;
  if (n === 0) return 0;
  if (n < 2) return '<2';
  if (n <= 10) return Math.round(n / 2) * 2;
  if (n <= 50) return Math.round(n / 5) * 5;
  return Math.round(n / 10) * 10;
};

// ─── Nutrition Facts Panel ────────────────────────────────────────────────────

const SAMPLE_NF = {
  servingSize: { en: '1 piece (85g)', es: '1 pieza (85g)' },
  servingsPerContainer: { en: 'About 4', es: 'Acerca de 4' },
  calories: 230,
  totalFat: { g: 8, dv: 10 },
  saturatedFat: { g: 5, dv: 25 },
  transFat: { g: 0 },
  cholesterol: { mg: 20, dv: 7 },
  sodium: { mg: 480, dv: 21 },
  totalCarbohydrate: { g: 30, dv: 11 },
  dietaryFiber: { g: '<1', dv: 3 },
  totalSugars: { g: '<1' },
  addedSugars: { g: 0, dv: 0 },
  protein: { g: 10 },
  vitaminD: { mcg: 1.9, dv: 10 },
  calcium: { mg: 130, dv: 10 },
  iron: { mg: 0.3, dv: 2 },
  potassium: { mg: 100, dv: 2 },
};

function NutritionFactsPanel({ nf: nfProp, isDesign, fontSize = 7, bilingual = true }) {
  const nf = (isDesign && !nfProp) ? SAMPLE_NF : (nfProp || SAMPLE_NF);

  const s = {
    panel: {
      fontFamily: 'Arial, Helvetica, sans-serif',
      fontSize,
      lineHeight: 1.2,
      border: '1px solid black',
      padding: '2px 3px',
      backgroundColor: 'white',
      width: '100%',
      boxSizing: 'border-box',
    },
    // FDA CFR 101.9: "Nutrition Facts" headline — bold, italic, hairline bottom rule
    headerTitle: {
      fontWeight: 'bold',
      fontStyle: 'italic',
      fontSize: fontSize * 2.33,
      lineHeight: 1,
      borderBottom: '1px solid black',
      paddingBottom: 2,
      marginBottom: 2,
    },
    servingLine: { display: 'flex', justifyContent: 'space-between', fontSize },
    // 8px rule after serving size block per FDA standard
    thickBorder: { borderTop: '8px solid black', marginTop: 2, marginBottom: 2 },
    thinBorder:  { borderTop: '0.5px solid black', marginTop: 1, marginBottom: 1 },
    // 4px medium rule after calories row per FDA standard
    calorieRow: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      borderBottom: '4px solid black',
      paddingBottom: 2,
      marginBottom: 2,
    },
    nutrientRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize },
    dvPercent: { fontWeight: 'bold', whiteSpace: 'nowrap' },
    footnote: { fontSize: Math.max(4, fontSize * 0.83), marginTop: 2, lineHeight: 1.2 },
    microRow: { display: 'flex', justifyContent: 'space-between', fontSize },
  };

  const bi = (en, es) => bilingual ? `${en} / ${es}` : en;
  // Safely extract a string from either a plain string or a { en, es } object
  const str = (val, fallback = '—') => {
    if (!val && val !== 0) return fallback;
    if (typeof val === 'object') return val.en || val.es || fallback;
    return String(val) || fallback;
  };

  // FDA layout: "Label value" on left, "XX%" on right
  const NRow = ({ label, value, dv, indent = 0, bold = false, separator = true }) => (
    <>
      {separator && <div style={s.thinBorder} />}
      <div style={{ ...s.nutrientRow, paddingLeft: indent === 1 ? 8 : indent === 2 ? 14 : 0 }}>
        <span style={{ fontWeight: bold ? 'bold' : 'normal' }}>
          {label}{value !== undefined && <> <strong>{value}</strong></>}
        </span>
        {dv !== undefined && <span style={s.dvPercent}>{dv}%</span>}
      </div>
    </>
  );

  return (
    <div style={s.panel}>
      <div style={s.headerTitle}>{bi('Nutrition Facts', 'Datos de Nutrición')}</div>

      <div style={s.servingLine}>
        <span>{bi('Servings per container', 'Porciones por envase')}:</span>
        <span>{str(nf.servingsPerContainer)}</span>
      </div>
      <div style={{ ...s.servingLine, fontWeight: 'bold' }}>
        <span>{bi('Serving size', 'Tamaño de porción')}</span>
        <span>{str(nf.servingSize)}</span>
      </div>

      <div style={s.thickBorder} />
      {/* FDA: "Amount per serving" is bold, ~0.83x base size */}
      <div style={{ fontSize: Math.max(4, fontSize * 0.83), fontWeight: 'bold' }}>{bi('Amount Per Serving', 'Cantidad por ración')}</div>

      <div style={s.calorieRow}>
        {/* FDA: "Calories" label larger than body; flex:1 lets it wrap without crowding the value */}
        <div style={{ fontSize: fontSize * 1.4, fontWeight: 'bold', flex: 1, marginRight: 4 }}>{bilingual ? <>Calories<br />Energía</> : 'Calories'}</div>
        <div style={{ fontWeight: 'bold', lineHeight: 1, fontSize: fontSize * 3.0, flexShrink: 0 }}>
          {nf.calories ?? '—'}
          {bilingual && nf.calories != null && (
            <span style={{ marginLeft: 4 }}>({Math.round(nf.calories * 4.184)} kJ)</span>
          )}
        </div>
      </div>

      <div style={{ textAlign: 'right', fontSize: Math.max(4, fontSize - 0.5), marginBottom: 1 }}>
        % {bi('Daily Value*', 'Valor Diario*')}
      </div>

      <NRow label={bi('Total Fat', 'Grasa Total')}             value={`${nf.totalFat?.g ?? '—'}g`}            dv={nf.totalFat?.dv}            bold separator={false} />
      <NRow label={bi('Saturated Fat', 'Grasa Saturada')}      value={`${nf.saturatedFat?.g ?? '—'}g`}         dv={nf.saturatedFat?.dv}        indent={1} />
      <NRow label={bi('Trans Fat', 'Grasa Trans')}             value={`${nf.transFat?.g ?? '—'}g`}             indent={1} />
      <NRow label={bi('Cholesterol', 'Colesterol')}            value={`${nf.cholesterol?.mg ?? '—'}mg`}        dv={nf.cholesterol?.dv}         bold />
      <NRow label={bi('Sodium', 'Sodio')}                      value={`${nf.sodium?.mg ?? '—'}mg`}             dv={nf.sodium?.dv}              bold />
      <NRow label={bi('Total Carbohydrate', 'Carbohidrato Total')} value={`${nf.totalCarbohydrate?.g ?? '—'}g`} dv={nf.totalCarbohydrate?.dv}  bold />
      <NRow label={bi('Dietary Fiber', 'Fibra Dietética')}     value={`${nf.dietaryFiber?.g ?? '—'}g`}         dv={nf.dietaryFiber?.dv}        indent={1} />
      <NRow label={bi('Total Sugars', 'Azúcares Totales')}     value={`${nf.totalSugars?.g ?? '—'}g`}          indent={1} />
      <NRow label={`${bi('Includes', 'Incluye')} ${nf.addedSugars?.g ?? '—'}g ${bi('Added Sugars', 'Azúcares Añadidos')}`} dv={nf.addedSugars?.dv} indent={2} />
      <NRow label={bi('Protein', 'Proteína')}                  value={`${nf.protein?.g ?? '—'}g`}              bold />

      <div style={{ borderTop: '8px solid black', marginTop: 2, marginBottom: 2 }} />

      <div style={s.microRow}><span>{bi('Vitamin D', 'Vitamina D')} {nf.vitaminD?.mcg ?? '—'}mcg</span><span style={s.dvPercent}>{fdaRoundMicroDV(nf.vitaminD?.dv) ?? '—'}%</span></div>
      <div style={s.thinBorder} />
      <div style={s.microRow}><span>{bi('Calcium', 'Calcio')} {nf.calcium?.mg ?? '—'}mg</span><span style={s.dvPercent}>{fdaRoundMicroDV(nf.calcium?.dv) ?? '—'}%</span></div>
      <div style={s.thinBorder} />
      <div style={s.microRow}><span>{bi('Iron', 'Hierro')} {nf.iron?.mg ?? '—'}mg</span><span style={s.dvPercent}>{fdaRoundMicroDV(nf.iron?.dv) ?? '—'}%</span></div>
      <div style={s.thinBorder} />
      <div style={s.microRow}><span>{bi('Potassium', 'Potasio')} {nf.potassium?.mg ?? '—'}mg</span><span style={s.dvPercent}>{fdaRoundMicroDV(nf.potassium?.dv) ?? '—'}%</span></div>

      <div style={s.thinBorder} />
      <div style={s.footnote}>
        * {bi(
          'The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.',
          'El % de Valor Diario (VD) le indica cuánto de un nutriente en una porción de alimento contribuye a la dieta diaria. Se usan 2,000 calorías al día para consejos generales de nutrición.'
        )}
      </div>
    </div>
  );
}

// ─── Nutrition Facts — Tabular (horizontal) format ────────────────────────────
// FDA 21 CFR 101.9(d)(11)(ii): side-by-side two-column layout; nutrients in rows.

function NutritionFactsTabular({ nf: nfProp, isDesign, fontSize = 7, bilingual = true }) {
  const nf = (isDesign && !nfProp) ? SAMPLE_NF : (nfProp || SAMPLE_NF);

  const bi  = (en, es) => bilingual ? `${en} / ${es}` : en;
  const str = (val, fb = '—') => {
    if (!val && val !== 0) return fb;
    if (typeof val === 'object') return val.en || val.es || fb;
    return String(val) || fb;
  };

  const border   = '1px solid black';
  const cellStyle = { borderRight: border, padding: '1px 3px', flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize };
  const lastCell  = { padding: '1px 3px', flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize };
  const rowStyle  = { display: 'flex', borderBottom: border };
  const label     = (txt, val, dv, bold) => (
    <span style={{ fontWeight: bold ? 'bold' : 'normal' }}>
      {txt}{val !== undefined && <> <strong>{val}</strong></>}
      {dv !== undefined && <> <strong>({dv}%DV)</strong></>}
    </span>
  );

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', border, fontSize, lineHeight: 1.2, backgroundColor: 'white', width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ borderBottom: '4px solid black', padding: '2px 3px' }}>
        <span style={{ fontWeight: 'bold', fontStyle: 'italic', fontSize: fontSize * 1.8, marginRight: 8 }}>
          {bi('Nutrition Facts', 'Datos de Nutrición')}
        </span>
        <span style={{ fontSize }}>{bi('Serving size', 'Tamaño de porción')}: <strong>{str(nf.servingSize)}</strong></span>
        <span style={{ fontSize, marginLeft: 10 }}>{bi('Servings per container', 'Porciones por envase')}: <strong>{str(nf.servingsPerContainer)}</strong></span>
      </div>

      {/* Calories row */}
      <div style={{ ...rowStyle, borderBottom: '4px solid black', padding: '1px 3px', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold', fontSize: fontSize * 1.2, flex: 1 }}>{bi('Calories', 'Calorías')}</span>
        <span style={{ fontWeight: 'bold', fontSize: fontSize * 2.2 }}>{nf.calories ?? '—'}</span>
        {bilingual && nf.calories != null && <span style={{ fontSize, marginLeft: 4 }}>({Math.round(nf.calories * 4.184)} kJ)</span>}
      </div>

      {/* Nutrient rows — two nutrients per row */}
      {[
        [
          { lbl: bi('Total Fat', 'Grasa Total'),             val: `${nf.totalFat?.g ?? '—'}g`,        dv: nf.totalFat?.dv,             bold: true },
          { lbl: bi('Saturated Fat', 'Grasa Saturada'),      val: `${nf.saturatedFat?.g ?? '—'}g`,     dv: nf.saturatedFat?.dv },
        ],
        [
          { lbl: bi('Trans Fat', 'Grasa Trans'),             val: `${nf.transFat?.g ?? '—'}g` },
          { lbl: bi('Cholesterol', 'Colesterol'),            val: `${nf.cholesterol?.mg ?? '—'}mg`,    dv: nf.cholesterol?.dv,          bold: true },
        ],
        [
          { lbl: bi('Sodium', 'Sodio'),                      val: `${nf.sodium?.mg ?? '—'}mg`,         dv: nf.sodium?.dv,               bold: true },
          { lbl: bi('Total Carbohydrate', 'Carbohidrato Total'), val: `${nf.totalCarbohydrate?.g ?? '—'}g`, dv: nf.totalCarbohydrate?.dv, bold: true },
        ],
        [
          { lbl: bi('Dietary Fiber', 'Fibra Dietética'),    val: `${nf.dietaryFiber?.g ?? '—'}g`,     dv: nf.dietaryFiber?.dv },
          { lbl: bi('Total Sugars', 'Azúcares Totales'),    val: `${nf.totalSugars?.g ?? '—'}g` },
        ],
        [
          { lbl: `${bi('Added Sugars', 'Azúcares Añadidos')} ${nf.addedSugars?.g ?? '—'}g`, dv: nf.addedSugars?.dv },
          { lbl: bi('Protein', 'Proteína'),                 val: `${nf.protein?.g ?? '—'}g`,           bold: true },
        ],
      ].map((pair, ri) => (
        <div key={ri} style={rowStyle}>
          <div style={cellStyle}>{label(pair[0].lbl, pair[0].val, pair[0].dv, pair[0].bold)}</div>
          <div style={lastCell}>{label(pair[1].lbl, pair[1].val, pair[1].dv, pair[1].bold)}</div>
        </div>
      ))}

      {/* Micronutrients */}
      <div style={{ ...rowStyle, borderTop: '4px solid black', borderBottom: 'none', flexWrap: 'wrap' }}>
        {[
          [bi('Vitamin D', 'Vit. D'), `${nf.vitaminD?.mcg ?? '—'}mcg`, nf.vitaminD?.dv],
          [bi('Calcium', 'Calcio'),   `${nf.calcium?.mg ?? '—'}mg`,     nf.calcium?.dv],
          [bi('Iron', 'Hierro'),      `${nf.iron?.mg ?? '—'}mg`,         nf.iron?.dv],
          [bi('Potassium', 'Potasio'),`${nf.potassium?.mg ?? '—'}mg`,   nf.potassium?.dv],
        ].map(([name, amt, dv], i, arr) => (
          <div key={i} style={{ ...( i < arr.length - 1 ? cellStyle : lastCell), flex: 'none', width: `${100 / arr.length}%`, boxSizing: 'border-box' }}>
            <span>{name} {amt}</span>
            <strong>{fdaRoundMicroDV(dv) ?? '—'}%</strong>
          </div>
        ))}
      </div>

      <div style={{ fontSize: Math.max(4, fontSize * 0.8), padding: '2px 3px', borderTop: border }}>
        * {bi(
          'The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.',
          'El % de Valor Diario (VD) indica cuánto de un nutriente en una porción contribuye a la dieta diaria. Se usan 2,000 calorías al día para consejos generales de nutrición.'
        )}
      </div>
    </div>
  );
}

// ─── Nutrition Facts — Linear (condensed) format ──────────────────────────────
// FDA 21 CFR 101.9(d)(11)(iii): for packages with <12 sq in total surface area.
// All nutrient data runs inline, separated by bullets.

function NutritionFactsLinear({ nf: nfProp, isDesign, fontSize = 7, bilingual = true }) {
  const nf = (isDesign && !nfProp) ? SAMPLE_NF : (nfProp || SAMPLE_NF);

  const bi  = (en, es) => bilingual ? `${en}/` + es : en;
  const str = (val, fb = '—') => {
    if (!val && val !== 0) return fb;
    if (typeof val === 'object') return val.en || val.es || fb;
    return String(val) || fb;
  };

  const sep = <span style={{ margin: '0 3px', color: '#555' }}>·</span>;
  const dv  = (val) => val !== undefined ? <> ({val}%DV)</> : null;

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', border: '1px solid black', fontSize, lineHeight: 1.4, backgroundColor: 'white', width: '100%', boxSizing: 'border-box', padding: '2px 4px' }}>
      <span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>{bilingual ? 'Nutrition Facts/Datos de Nutrición' : 'Nutrition Facts'}</span>
      {sep}
      <span>{bi('Serving size', 'Tamaño de porción')} <strong>{str(nf.servingSize)}</strong></span>
      {sep}
      <span>{bi('Servings', 'Porciones')}: <strong>{str(nf.servingsPerContainer)}</strong></span>
      {sep}
      <span><strong>{bi('Calories', 'Calorías')} {nf.calories ?? '—'}</strong>
        {bilingual && nf.calories != null && <> ({Math.round(nf.calories * 4.184)} kJ)</>}
      </span>
      {sep}
      <span>{bi('Total Fat', 'Grasa Total')} <strong>{nf.totalFat?.g ?? '—'}g</strong>{dv(nf.totalFat?.dv)}</span>
      {sep}
      <span>{bi('Saturated Fat', 'Grasa Saturada')} <strong>{nf.saturatedFat?.g ?? '—'}g</strong>{dv(nf.saturatedFat?.dv)}</span>
      {sep}
      <span>{bi('Trans Fat', 'Grasa Trans')} <strong>{nf.transFat?.g ?? '—'}g</strong></span>
      {sep}
      <span>{bi('Cholesterol', 'Colesterol')} <strong>{nf.cholesterol?.mg ?? '—'}mg</strong>{dv(nf.cholesterol?.dv)}</span>
      {sep}
      <span>{bi('Sodium', 'Sodio')} <strong>{nf.sodium?.mg ?? '—'}mg</strong>{dv(nf.sodium?.dv)}</span>
      {sep}
      <span>{bi('Total Carb.', 'Carbohidrato Total')} <strong>{nf.totalCarbohydrate?.g ?? '—'}g</strong>{dv(nf.totalCarbohydrate?.dv)}</span>
      {sep}
      <span>{bi('Fiber', 'Fibra')} <strong>{nf.dietaryFiber?.g ?? '—'}g</strong>{dv(nf.dietaryFiber?.dv)}</span>
      {sep}
      <span>{bi('Total Sugars', 'Azúcares')} <strong>{nf.totalSugars?.g ?? '—'}g</strong></span>
      {sep}
      <span>{bi('Added Sugars', 'Az. Añadidos')} <strong>{nf.addedSugars?.g ?? '—'}g</strong>{dv(nf.addedSugars?.dv)}</span>
      {sep}
      <span>{bi('Protein', 'Proteína')} <strong>{nf.protein?.g ?? '—'}g</strong></span>
      {sep}
      <span>{bi('Vit. D', 'Vit. D')} {nf.vitaminD?.mcg ?? '—'}mcg ({fdaRoundMicroDV(nf.vitaminD?.dv) ?? '—'}%)</span>
      {sep}
      <span>{bi('Calcium', 'Calcio')} {nf.calcium?.mg ?? '—'}mg ({fdaRoundMicroDV(nf.calcium?.dv) ?? '—'}%)</span>
      {sep}
      <span>{bi('Iron', 'Hierro')} {nf.iron?.mg ?? '—'}mg ({fdaRoundMicroDV(nf.iron?.dv) ?? '—'}%)</span>
      {sep}
      <span>{bi('Potassium', 'Potasio')} {nf.potassium?.mg ?? '—'}mg ({fdaRoundMicroDV(nf.potassium?.dv) ?? '—'}%)</span>
      {sep}
      <span style={{ fontSize: Math.max(4, fontSize * 0.85) }}>*5% or less is Low, 20% or more is High</span>
    </div>
  );
}

// ─── UPC Barcode ──────────────────────────────────────────────────────────────

function UpcBarcode({ upc }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current) return;
    const code = (upc || '').replace(/\D/g, ''); // strip everything except digits
    // Auto-detect format: 14 digits → ITF-14 (case barcode), otherwise UPC-A
    const format = code.length === 14 ? 'ITF14' : 'UPC';
    const minLength = format === 'ITF14' ? 14 : 11;
    if (code.length >= minLength) {
      try {
        JsBarcode(svgRef.current, code, {
          format,
          width: 1.2,
          height: 40,
          displayValue: false,
          margin: 0,
        });
        return;
      } catch {
        // Check digit may be wrong — strip it and let JsBarcode recalculate
        const retryLen = format === 'ITF14' ? 13 : (format === 'UPC' && code.length === 12 ? 11 : 0);
        if (retryLen > 0) {
          try {
            JsBarcode(svgRef.current, code.slice(0, retryLen), {
              format,
              width: 1.2,
              height: 40,
              displayValue: false,
              margin: 0,
            });
            return;
          } catch { /* fall through to placeholder */ }
        }
      }
    }
    // Placeholder when UPC is empty or invalid
    svgRef.current.innerHTML = '';
    svgRef.current.setAttribute('width', '100%');
    svgRef.current.setAttribute('height', '50');
  }, [upc]);

  return <svg ref={svgRef} style={{ width: '100%', maxWidth: '100%', height: '100%', maxHeight: '100%', display: 'block' }} />;
}

// ─── GS1-128 Case Barcode ─────────────────────────────────────────────────────
// Encodes AI(01) GTIN-14, AI(3202) case net weight in lb, AI(15) best-by date, AI(21) serial
// number into one Code 128 symbol — see docs/PRODUCT_LABELS_MODULE.md. Every AI here is fixed
// length except the trailing serial, so per GS1 rules no FNC1 field separators are needed beyond
// the single leading one JsBarcode's `ean128` option inserts automatically.
function buildGs1128Data({ gtin, weightLb, bestByISO, serial }) {
  const gtinDigits = (gtin || '').replace(/\D/g, '');
  const weightNum = Math.round((parseFloat(weightLb) || 0) * 100);
  const weightStr = String(Math.max(0, weightNum)).padStart(6, '0').slice(-6);
  let dateStr = '';
  if (bestByISO) {
    const [y, m, d] = String(bestByISO).slice(0, 10).split('-');
    if (y && m && d) dateStr = `${y.slice(-2)}${m}${d}`;
  }
  const serialStr = (serial || '').toString().replace(/[^A-Za-z0-9-]/g, '').slice(0, 20);

  const missing = [];
  if (gtinDigits.length !== 14) missing.push('case GTIN');
  if (weightNum <= 0) missing.push('case weight (lb)');
  if (dateStr.length !== 6) missing.push('best-by date');
  if (!serialStr.length) missing.push('serial/lot code');

  return {
    isValid: missing.length === 0,
    missing,
    raw: `01${gtinDigits.padStart(14, '0')}3202${weightStr}15${dateStr || '000000'}21${serialStr}`,
    human: `(01)${gtinDigits.padStart(14, '0')}(3202)${weightStr}(15)${dateStr || '------'}(21)${serialStr}`,
  };
}

// Design-time sample so the block shows something meaningful while laying out a template,
// before any real print-job values exist — mirrors the WFF case label spec example.
const GS1_128_SAMPLE = { gtin: '10842535180386', weightLb: '28.00', bestByISO: '2023-03-20', serial: '22082-001' };

function Gs1128Barcode({ gtin, weightLb, bestByISO, serial, isDesign }) {
  const svgRef = useRef(null);
  const useSample = isDesign && !gtin && !weightLb && !bestByISO && !serial;
  const source = useSample ? GS1_128_SAMPLE : { gtin, weightLb, bestByISO, serial };
  const data = buildGs1128Data(source);

  useEffect(() => {
    if (!svgRef.current) return;
    if (!data.isValid) { svgRef.current.innerHTML = ''; return; }
    try {
      JsBarcode(svgRef.current, data.raw, {
        format: 'CODE128',
        ean128: true,
        width: 1.2,
        height: 40,
        displayValue: false,
        margin: 0,
      });
    } catch {
      svgRef.current.innerHTML = '';
    }
  }, [data.raw, data.isValid]);

  if (!data.isValid) {
    return (
      <div style={{
        width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, color: '#b91c1c', textAlign: 'center', border: '1px dashed #b91c1c', padding: 4,
      }}>
        GS1-128 barcode incomplete — missing: {data.missing.join(', ')}
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <svg ref={svgRef} style={{ width: '100%', maxWidth: '100%', flex: 1, display: 'block' }} />
      <div style={{ fontSize: 8, fontFamily: 'monospace', marginTop: 2, whiteSpace: 'nowrap' }}>{data.human}</div>
    </div>
  );
}

// ─── Block Renderer ───────────────────────────────────────────────────────────

function renderBlock(block, content, isDesign) {
  const { type, style = {}, id } = block;

  const baseStyle = {
    fontSize: style.fontSize || undefined,
    fontWeight: style.fontWeight || undefined,
    fontStyle: style.fontStyle || undefined,
    textAlign: style.textAlign || undefined,
    marginBottom: style.marginBottom !== undefined ? style.marginBottom : undefined,
    border: style.border || undefined,
    padding: style.padding || undefined,
    whiteSpace: style.whiteSpace || undefined,
  };

  switch (type) {
    case 'product_name': {
      const text = content?.productName || (isDesign ? 'PRODUCT NAME' : '');
      return <div key={id} style={baseStyle}>{text}</div>;
    }

    case 'net_weight': {
      const text = content?.netWeight || (isDesign ? 'Net Weight: XX oz (XXX g)' : '');
      return <div key={id} style={baseStyle}>{text}</div>;
    }

    case 'po_number': {
      // Dynamic per print job (see docs/PRODUCT_LABELS_MODULE.md) — same reasoning as lot_exp_box's
      // lot/exp/prodDate fields, never baked into the label version's static content.
      const text = content?.po || (isDesign ? 'PO 8038' : '');
      return <div key={id} style={baseStyle}>{text}</div>;
    }

    case 'plu_sku': {
      // Customer's own product code (e.g. Wild Fork's "PLU") — fixed per label content/variant,
      // NOT per print job. Distinct from po_number above, which is genuinely order-specific.
      const text = content?.pluSku || (isDesign ? 'PLU 8038' : '');
      return <div key={id} style={baseStyle}>{text}</div>;
    }

    case 'storage_banner': {
      const text = content?.storageInstructions || 'KEEP FROZEN — Store at 0°F (-18°C) or Below';
      return <div key={id} style={baseStyle}>{text}</div>;
    }

    case 'cooking_instructions': {
      const instructions = content?.cookingInstructions || (isDesign
        ? 'CONVENTIONAL OVEN: Preheat oven to 425°F (220°C). Remove all packaging. Place pizza directly on center oven rack. Bake 14–17 min until cheese is melted and crust is golden. Internal temp must reach 165°F (74°C). Do not leave oven unattended.'
        : '');
      return (
        <div key={id} style={baseStyle}>
          <span style={{ fontWeight: 'bold' }}>COOKING INSTRUCTIONS: </span>
          {instructions}
        </div>
      );
    }

    case 'ingredients': {
      const ingredientText = content?.ingredients || (isDesign
        ? 'Enriched Wheat Flour (Flour, Niacin, Reduced Iron, Thiamine Mononitrate, Riboflavin, Folic Acid), Water, Mozzarella Cheese (Pasteurized Milk, Cheese Cultures, Salt, Enzymes), Tomato Puree (Water, Tomato Paste), Sugar, Soybean Oil, Salt, Yeast, Spices.'
        : '');
      return (
        <div key={id} style={baseStyle}>
          <span style={{ fontWeight: 'bold' }}>INGREDIENTS: </span>
          {ingredientText}
        </div>
      );
    }

    case 'allergen_statement': {
      const allergens = content?.allergens || (isDesign ? 'Wheat, Milk' : '');
      return (
        <div key={id} style={baseStyle}>
          <span style={{ fontWeight: 'bold' }}>CONTAINS: </span>
          {allergens}
        </div>
      );
    }

    case 'upc_barcode': {
      // A label template is either 'product' or 'case' type, and only ever populates the
      // matching content field (see LabelDetailPage.js) — so no per-block type is needed.
      const upcVal = content?.upc || content?.caseUpc;
      return (
        <div key={id} style={{ ...baseStyle, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <UpcBarcode upc={upcVal} />
        </div>
      );
    }

    case 'gs1_128_barcode': {
      return (
        <div key={id} style={{ ...baseStyle, width: '100%', height: '100%', overflow: 'hidden' }}>
          <Gs1128Barcode
            gtin={content?.caseUpc}
            weightLb={content?.gs1CaseWeightLb}
            bestByISO={content?.gs1BestByISO}
            serial={content?.gs1Serial}
            isDesign={isDesign}
          />
        </div>
      );
    }

    case 'upc_text': {
      const upcVal = content?.upc || content?.caseUpc || (isDesign ? '0 12345 67890 5' : '');
      return <div key={id} style={baseStyle}>{upcVal}</div>;
    }

    case 'manufacturer_info': {
      const info = content?.manufacturerInfo || (isDesign
        ? 'Manufactured by Pizzacini Foods, LLC\n1 Artisan Way, Chicago, IL 60601\n(312) 555-0100 · pizzacini.com'
        : '');
      const coo = content?.countryOfOrigin || (isDesign ? 'PRODUCT OF USA' : '');
      return (
        <div key={id} style={{ ...baseStyle, whiteSpace: 'pre-line' }}>
          {info}
          {coo ? <div style={{ marginTop: 2 }}>{coo}</div> : null}
        </div>
      );
    }

    case 'brand_logo': {
      const wrapperStyle = {
        ...baseStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      };
      if (content?.logoUrl) {
        return (
          <div key={id} style={wrapperStyle}>
            <img
              src={content.logoUrl}
              alt="Brand Logo"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
            />
          </div>
        );
      }
      if (!isDesign) return null;
      return (
        <div key={id} style={{ ...wrapperStyle, fontWeight: 'bold', letterSpacing: 4, border: '2px dashed #ccc', padding: '6px 4px', color: '#555', fontSize: 10 }}>
          BRAND LOGO
        </div>
      );
    }

    case 'lot_exp_box': {
      // expBlockTitle: null = fall back to expirationLabel; '' = no title; string = custom
      const bestBy = content?.expBlockTitle != null
        ? content.expBlockTitle
        : (content?.expirationLabel || 'BEST BY:');
      const lot    = content?.lot || (isDesign ? 'Lot.: ______' : 'Lot.: ______');
      const exp    = content?.exp || (isDesign ? 'Exp.: ______' : 'Exp.: ______');
      // content.showProdDate overrides the block-level setting (set by variant)
      const showProdDate = content?.showProdDate !== undefined
        ? !!content.showProdDate
        : block.showProdDate === true;
      // content.prodDateLabel overrides the block-level label (set by variant)
      const prodDateLabel = content?.prodDateLabel || block.prodDateLabel || 'P.';
      const prodDate = content?.prodDate || '______';
      return (
        <div key={id} style={{ ...baseStyle, border: '2px solid black', padding: '4px 6px', width: '100%', boxSizing: 'border-box' }}>
          {bestBy !== '' && <div style={{ fontWeight: 'bold' }}>{bestBy}</div>}
          <div>{lot}</div>
          <div>{exp}</div>
          {showProdDate && (
            <div>{prodDateLabel} {prodDate}</div>
          )}
        </div>
      );
    }

    case 'qr_code': {
      const lot = typeof content?.lot === 'string' ? content.lot.replace(/^Lot:\s*/i, '').trim() : '';
      const productId = content?.productId || null;
      const traceUrl = lot
        ? `${window.location.origin}/trace/${lot}${productId ? `/${productId}` : ''}`
        : null;
      const qrSize = Number(style.qrSize) || 64;
      return (
        <div key={id} style={{
          ...baseStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: style.padding ?? 2,
        }}>
          {traceUrl
            ? <QRCodeSVG value={traceUrl} size={qrSize} level="M" />
            : isDesign
              ? <div style={{ width: qrSize, height: qrSize, border: '2px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#aaa' }}>QR</div>
              : null
          }
        </div>
      );
    }

    case 'nutrition_facts': {
      const nfBilingual = content?.nutritionFacts?.bilingual !== undefined
        ? content.nutritionFacts.bilingual !== false
        : block.bilingual !== false;
      const nfFormat = block.nfFormat || 'vertical';
      const NfComponent = nfFormat === 'tabular' ? NutritionFactsTabular
                        : nfFormat === 'linear'   ? NutritionFactsLinear
                        : NutritionFactsPanel;
      return (
        <div key={id} style={{ ...baseStyle, width: '100%' }}>
          <NfComponent
            nf={content?.nutritionFacts}
            isDesign={isDesign}
            fontSize={style.fontSize || 8}
            bilingual={nfBilingual}
          />
        </div>
      );
    }

    case 'country_of_origin': {
      const coo = content?.countryOfOrigin || (isDesign ? 'PRODUCT OF USA' : '');
      return <div key={id} style={baseStyle}>{coo}</div>;
    }

    case 'custom_text': {
      // variant-level customTextOverride wins; then per-block customTexts; then design default
      const customText = content?.customTextOverride ?? content?.customTexts?.[id] ?? block.customText ?? '';
      return <div key={id} style={baseStyle}>{customText || (isDesign ? '[Custom text]' : '')}</div>;
    }

    case 'custom_image': {
      const imgUrl = content?.customImages?.[id] || block.imageUrl;
      return (
        <div key={id} style={{ ...baseStyle, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: style.textAlign === 'left' ? 'flex-start' : style.textAlign === 'right' ? 'flex-end' : 'center', overflow: 'hidden' }}>
          {imgUrl
            ? <img src={imgUrl} alt={block.imageName || 'Custom image'} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
            : isDesign
              ? <div style={{ width: '100%', height: '100%', border: '2px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#aaa', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 18 }}>🖼</span>
                  <span>{block.imageName || 'Custom Image'}</span>
                </div>
              : null
          }
        </div>
      );
    }

    case 'usda_inspection_mark': {
      const imgUrl = block.imageUrl;
      return (
        <div key={id} style={{ ...baseStyle, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {imgUrl
            ? <img src={imgUrl} alt="USDA Inspection Mark" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
            : isDesign
              ? <div style={{ width: '100%', height: '100%', border: '2px dashed #b91c1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#b91c1c', fontWeight: 'bold', textAlign: 'center', padding: 2 }}>
                  USDA MARK MISSING
                </div>
              : null
          }
        </div>
      );
    }

    case 'divider':
      return <hr key={id} style={{ border: 'none', borderTop: '1px solid black', margin: `${style.marginBottom ?? 2}px 0`, width: '100%' }} />;

    default:
      return <div key={id} style={{ ...baseStyle, color: '#999', fontSize: 9 }}>[{type}]</div>;
  }
}

// ─── Layout Renderers ─────────────────────────────────────────────────────────

// Renders a leaf column (has blocks[], no sub-rows)
function renderLeafColumn(col, content, isDesign) {
  return (
    <div
      key={col.id}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: col.flex || 1,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        padding: '0 4px',
      }}
    >
      {(col.blocks || []).map(block => renderBlock(block, content, isDesign))}
    </div>
  );
}

// Renders a row (flex row of leaf columns)
function renderRow(row, content, isDesign) {
  return (
    <div
      key={row.id}
      style={{
        display: 'flex',
        flexDirection: 'row',
        flex: row.flex || 1,
        minHeight: 0,
      }}
    >
      {(row.columns || []).map(col => renderLeafColumn(col, content, isDesign))}
    </div>
  );
}

// Renders a top-level panel:
//   - panel.rows  → vertical stack of rows (each row has sub-columns)
//   - panel.blocks → blocks rendered directly, spanning full panel height
function renderPanel(panel, content, isDesign) {
  const panelStyle = {
    display: 'flex',
    flexDirection: 'column',
    flex: panel.flex || 1,
    minWidth: 0,
    overflow: 'hidden',
    padding: '0 2px',
  };

  if (panel.rows && panel.rows.length > 0) {
    return (
      <div key={panel.id} style={panelStyle}>
        {panel.rows.map(row => renderRow(row, content, isDesign))}
      </div>
    );
  }

  // Direct blocks — spans the full panel height
  return (
    <div key={panel.id} style={{ ...panelStyle, minHeight: 0 }}>
      {(panel.blocks || []).map(block => renderBlock(block, content, isDesign))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function LabelRenderer({ design, content, scale = 1, isDesign = false }) {
  if (!design) return null;

  const pxWidth  = (design.width  || 6) * 96;
  const pxHeight = (design.height || 4) * 96;

  // Canvas mode: flat list of absolutely-positioned blocks
  if (Array.isArray(design.canvas)) {
    return (
      <div style={{ width: pxWidth * scale, height: pxHeight * scale, overflow: 'hidden' }}>
        <div style={{
          width: pxWidth,
          height: pxHeight,
          backgroundColor: 'white',
          overflow: 'hidden',
          position: 'relative',
          fontFamily: 'Arial, sans-serif',
          boxSizing: 'border-box',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}>
          {design.canvas.map(block => (
            <div key={block.id} style={{
              position: 'absolute',
              left: block.x * 96,
              top:  block.y * 96,
              width: block.w * 96,
              height: block.h * 96,
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}>
              {renderBlock(block, content, isDesign)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Legacy flex mode: top-level columns (panels) or rows
  const hasColumns = Array.isArray(design.columns) && design.columns.length > 0;

  return (
    <div style={{ width: pxWidth * scale, height: pxHeight * scale, overflow: 'hidden' }}>
      <div
        style={{
          width: pxWidth,
          height: pxHeight,
          backgroundColor: 'white',
          overflow: 'hidden',
          position: 'relative',
          fontFamily: 'Arial, sans-serif',
          boxSizing: 'border-box',
          padding: 8,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          display: 'flex',
          flexDirection: hasColumns ? 'row' : 'column',
        }}
      >
        {hasColumns
          ? design.columns.map(panel => renderPanel(panel, content, isDesign))
          : (design.rows || []).map(row => renderRow(row, content, isDesign))
        }
      </div>
    </div>
  );
}

export default LabelRenderer;
